/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2, useMediaQuery } from '@coursera/cds-core';
import { HistoryIcon } from '@coursera/cds-icons';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import LearningProductCard from 'bundles/product-card/components/LearningProductCard';
import LearningProductCardImage from 'bundles/product-card/components/LearningProductCardImage';
import {
  convertDifficultyToEnum,
  getImagePropOverrides,
  getPartnerNamesAndLogos,
} from 'bundles/product-card/components/utils/productCardV2Utils';
import { LEARNING_PRODUCTS_ENUM_MAP, getTranslatedDifficulty } from 'bundles/search-common/constants';
import type { PRODUCT_DIFFICULTY_LEVELS } from 'bundles/search-common/constants';
import { type AutocompleteMode, RECENTLY_VIEWED_GROUP_ID } from 'bundles/search-common/constants/autocomplete';
import type { AutocompleteItem, AutocompleteItems } from 'bundles/search-common/utils/autocomplete';
import { getAutocompleteEventingData } from 'bundles/search-common/utils/autocomplete';

import _t from 'i18n!nls/search-common';

const styles = {
  cardContainer: css`
    height: 100%;
  `,

  clearIconButton: css`
    position: absolute;
    top: var(--cds-spacing-100);
    right: var(--cds-spacing-100);
    z-index: 10;
    opacity: 0;
    transition: opacity 0.2s ease;

    &:hover,
    &:focus {
      opacity: 1;
    }
  `,

  recentlyViewedContainer: css`
    display: flex;
    align-items: center;
    margin-bottom: 4px;
  `,

  historyIcon: css`
    margin-right: 8px;
  `,
};

type Props = {
  autocompleteState: AutocompleteMode;
  id: string;
  recentlyViewedItems: AutocompleteItems;
  suggestionIndex: number;
  item: AutocompleteItem;
  groupId: string;
  onRemove?: () => void;
};

const AutocompleteSearchProductCard = ({
  autocompleteState,
  id,
  recentlyViewedItems,
  suggestionIndex,
  item,
  groupId,
  onRemove,
}: Props) => {
  const eventingData = getAutocompleteEventingData(id, autocompleteState, recentlyViewedItems, suggestionIndex);
  const trackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_autocomplete_suggestions',
    eventingData,
    [],
    { fullyVisible: true, initialDelay: 1000 }
  );
  const isMobile = useMediaQuery('(max-width: 1060px)');
  const cardImageUrl = typeof item?.cardImageUrl === 'string' ? item.cardImageUrl : '';
  const fallbackImageUrl = typeof item?.imageUrl === 'string' ? item.imageUrl : '';
  const partnerLogo = typeof item?.partnerLogo === 'string' ? item.partnerLogo : undefined;
  const imageUrl = cardImageUrl || fallbackImageUrl;
  const partners = item?.partnerName
    ? [{ id: item.partnerName, name: item.partnerName, logo: fallbackImageUrl }]
    : undefined;
  const productCardPartners = partners && getPartnerNamesAndLogos(partners, false, [fallbackImageUrl]);

  // Set image dimensions based on screen size
  const baseImageParams = getImagePropOverrides(partners || [], imageUrl);
  const imageParams = isMobile ? { ...baseImageParams, width: 127, height: 72 } : baseImageParams;

  // Only show badge if isRecentlyViewed is true AND groupId is not RECENTLY_VIEWED_GROUP_ID (to avoid redundancy)
  const shouldShowBadge = item.isRecentlyViewed === true && groupId !== RECENTLY_VIEWED_GROUP_ID;

  const recentlyViewedBadge = shouldShowBadge ? (
    <div data-testid="product-card-recently-viewed" css={styles.recentlyViewedContainer}>
      <HistoryIcon css={styles.historyIcon} aria-hidden={true} color="interactive" />
      <Typography2 component="p" variant="bodyTertiary" color="emphasis.primary">
        {_t('Recently viewed')}
      </Typography2>
    </div>
  ) : undefined;

  return (
    <div ref={trackingRef} css={styles.cardContainer} data-group={groupId}>
      <LearningProductCard
        name={item.name}
        id={eventingData.product?.id}
        ImageComponent={
          <LearningProductCardImage
            imageUrl={imageUrl}
            enableFluidWidthForLazyImg={true}
            prioritizeImageLoading={false}
            enableLazyLoading={false}
            imageParams={imageParams}
          />
        }
        partners={productCardPartners}
        linkProps={{ href: item.path || '#' }}
        autocompleteProductCardData={{
          cardImageUrl: imageUrl || undefined,
          partnerName: item?.partnerName,
          partnerLogo,
          difficulty: convertDifficultyToEnum(item?.difficulty),
        }}
        preBadgeContent={recentlyViewedBadge}
        badgeConfig={{
          hideCreditBadge: true,
        }}
        statusTagConfig={{
          hideAllStatusTags: true,
        }}
        variant="grid"
        size="compact"
        cardIndex={suggestionIndex}
        indexName="autocomplete_recently_viewed"
        productType={LEARNING_PRODUCTS_ENUM_MAP[item?.productType || '']}
        productDifficultyLevel={
          item?.difficulty
            ? (getTranslatedDifficulty(
                item.difficulty
              ) as (typeof PRODUCT_DIFFICULTY_LEVELS)[keyof typeof PRODUCT_DIFFICULTY_LEVELS])
            : undefined
        }
        trackingProps={{
          trackedLinkProps: {
            trackingName: 'click_autocomplete_suggestion',
            withVisibilityTracking: false,
            requireFullyVisible: false,
            data: eventingData,
          },
        }}
      />
    </div>
  );
};

export default AutocompleteSearchProductCard;
